/**
 * iOS WebView document-scroll policy (fix quick-260721).
 *
 * Background: 05-04 (CAP-06) disabled the WKWebView's own scroll globally at boot via
 * `Keyboard.setScroll({ isDisabled: true })` to stop iOS from scrolling the outer document
 * to reveal a focused input inside the Add-entry dialog — that native scroll drags
 * position:fixed dialog/header ancestors off-screen (the dialog does its own scrollIntoView
 * instead). But the plugin's setDisableScroll flips `webView.scrollView.scrollEnabled = NO`
 * for the ENTIRE app and installs a delegate that pins contentOffset to zero — so any normal
 * document-flow page (Categories, Settings) can never scroll on iOS, even though the page
 * content overflows the viewport.
 *
 * Fix: scope the disable to the calendar route only. The calendar (index route `/`) is a
 * position:fixed full-viewport screen that never needs document scroll and is the only place
 * the fixed Add-entry / entry-detail dialogs open, so the keyboard-reveal fix still applies
 * there. Every other route (Categories, Settings, the standalone entry form) is normal
 * document flow and must keep native scroll enabled.
 *
 * Native + iOS only (Android's keyboard resize handling doesn't disable the scroll view).
 */
import type { AnyRouter } from '@tanstack/react-router';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { isRunningInNativeShell } from './pwa';

/** The calendar is the only route that keeps the WebView's native scroll disabled. */
function shouldDisableScroll(pathname: string): boolean {
  return pathname === '/';
}

export function registerNativeScroll(router: AnyRouter): void {
  if (!isRunningInNativeShell() || Capacitor.getPlatform() !== 'ios') return;

  const apply = (pathname: string) => {
    Keyboard.setScroll({ isDisabled: shouldDisableScroll(pathname) }).catch(() => {});
  };

  // Apply for the initial route, then re-apply after every resolved navigation.
  apply(router.state.location.pathname);
  router.subscribe('onResolved', () => {
    apply(router.state.location.pathname);
  });
}
